import { Affix, Indicator, Menu, Text } from '@mantine/core';
import { useListState } from '@mantine/hooks';
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import React, { useContext, useEffect, useState } from 'react';
import { doc, writeBatch } from 'firebase/firestore';
import { notifications } from "@mantine/notifications";
import { IconX } from '@tabler/icons-react';
import { Camera, CloudUpload, Plus, Trash } from 'framework7-icons-plus/react';
import { byImageActivityOrder, Place, PlaceImage } from '../../lib/interfaces';
import { useFirebaseRefs } from '../../lib/firebase';
import { useIsWide } from '../../lib/hooks';
import { showModalAsync } from '../TSModal';
import { SheetsContext } from '../Sheets';
import { deleteImage } from '../ImageLightbox';
import { usePlaceImages } from '../SavedPlacesAppShell/PanelImageGallery';
import { TSCachedImg } from "../TSImageCache";

import classes from "./RearrangeImages.module.css";

export function RearrangeImages({ place, onAddPhoto }:
    { place: Place, onAddPhoto?: () => unknown }): JSX.Element {
    const firebaseRefs = useFirebaseRefs();
    const isWide = useIsWide();
    // Subscribe to the trip's photos live, rather than rendering a one-time
    // snapshot — otherwise photos added (or removed) while the sheet is open
    // never appear, since the sheet captures props at open time.
    const livePlaceImages = usePlaceImages({ docid: place.tripdocid ?? undefined }, place);
    const [ state, handlers ] = useListState<PlaceImage>([]);
    const [ openEdit, setOpenEdit ] = useState(false);
    const [ showEditButton, setShowEditButton ] = useState(true);
    const sheets = useContext(SheetsContext);

    // Re-sync the local (drag-reorderable) ordering whenever the *set* of photos
    // changes — i.e. one is added or deleted. Keying on the docid set (not the
    // array itself) means a reorder's own Firestore echo, which only changes
    // activityOrder, won't clobber an in-progress drag.
    const photoIdsKey = livePlaceImages.map(img => img.docid).sort().join(',');
    useEffect(() => {
        handlers.setState([ ...livePlaceImages ].sort(byImageActivityOrder));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ photoIdsKey ]);

    // Delete mode only makes sense while there are photos. Once the last one is
    // removed, leave delete mode so the "+" add-photo tile is shown again.
    useEffect(() => {
        if (openEdit && state.length === 0) {
            setOpenEdit(false);
        }
    }, [ openEdit, state.length ]);

    const items = openEdit
        ? state.map((item, index) => (
            <Draggable key={item.docid} index={index} draggableId={item.docid!} isDragDisabled>
                {(provided, snapshot) => (
                    <Indicator
                        onClick={async () => {
                            setShowEditButton(false);
                            const response = await showModalAsync(
                                sheets,
                                'Remove Photo?',
                                [
                                    { title: 'Cancel' },
                                    { title: 'Remove', id: 'remove', type: 'destructive' },
                                ]
                            );
                            if (response.type === 'choice' && response.choice.id === 'remove') {
                                deleteImage(firebaseRefs, item);
                                handlers.remove(index);
                                setShowEditButton(true);
                            }
                        }}
                        m='md'
                        label={<IconX size={18} />}
                        inline
                        color="red"
                        position="top-start"
                        size={30}
                        withBorder
                    >
                        <div
                            className={`${classes.item} ${snapshot.isDragging ? classes.itemDragging : ''}`}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                        >
                            <TSCachedImg
                                style={{ height: '80px', width: '80px' }}
                                src={item.url400h ?? item.url}
                                alt='Trip Image'
                                lazyLoading={false}
                            />
                        </div>
                    </Indicator>
                )}
            </Draggable>
        ))
        : state.map((item, index) => (
            <Draggable key={item.docid} index={index} draggableId={item.docid!}>
                {(provided, snapshot) => (
                    <div
                        className={`${classes.item} ${snapshot.isDragging ? classes.itemDragging : ''}`}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                    >
                        <TSCachedImg
                            style={{ height: '80px', width: '80px' }}
                            src={item.url400h ?? item.url}
                            alt='Trip Image'
                            lazyLoading={false}
                        />
                    </div>
                )}
            </Draggable>
        ));

    function writeReorder(result: PlaceImage[]) {
        const batch = writeBatch(firebaseRefs.firestore);
        //firebase batchwrite result
        result.forEach((img, i) => {
            const placeDoc = doc(firebaseRefs.firestore, 'trips', img.tripId!, 'pictures', img.docid!);
            batch.set(placeDoc, { activityOrder: i }, { merge: true });
        });
        batch.commit().catch((e: any) => notifications.show({ message: `Your photos did not reorder - ${e.message}` }));
    }

    function sortByTaken() {
        sortBy((a, b) => (a.phototaken?.toMillis() ?? 0) - (b.phototaken?.toMillis() ?? 0));
    }

    function sortByUploaded() {
        sortBy((a, b) => (a.docCreated?.toMillis() ?? 0) - (b.docCreated?.toMillis() ?? 0));
    }

    function sortBy(comparator: (a: PlaceImage, b: PlaceImage) => number) {
        const copy = [ ...state ];
        copy.sort(comparator);

        let arraysAreEqual = true;
        for (let i = 0; i < copy.length; i++) {
            if (copy[i] !== state[i]) {
                arraysAreEqual = false;
                break;
            }
        }

        if (!arraysAreEqual) {
            writeReorder(copy);
            handlers.setState(copy);
        }
    }

    return (<>
        <DragDropContext
            onDragEnd={({ destination, source }) => {
                // Dropped outside the list, or back in place — nothing to do.
                if (!destination || destination.index === source.index) {
                    return;
                }
                // Compute the new order once, then apply it locally and persist it.
                const reordered = Array.from(state);
                const [ moved ] = reordered.splice(source.index, 1);
                reordered.splice(destination.index, 0, moved);
                handlers.setState(reordered);
                writeReorder(reordered);
            }}
        >
            <Droppable droppableId="dnd-list" direction="horizontal">
                {(provided) => (
                    <div {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'start',
                            flexWrap: 'wrap',
                        }}>
                        {items}
                        {provided.placeholder}
                        {onAddPhoto && !openEdit && (
                            <div className={classes.item}>
                                <div
                                    role="button"
                                    tabIndex={0}
                                    aria-label="Add Photo"
                                    onClick={() => onAddPhoto()}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            onAddPhoto();
                                        }
                                    }}
                                    style={{
                                        height: '80px',
                                        width: '80px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Plus height={32} width={32} />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
        {showEditButton && <Affix position={{
            bottom: 20,
            // The Affix is fixed to the viewport, but on wide screens the sheet is inset to the
            // centred column (--tscroll-wide-sheet-inset clear each side), so keep the Edit button
            // inside the sheet's right edge rather than out in the margin.
            right: (isWide ?? false) ? 'calc(var(--tscroll-wide-sheet-inset) + 20px)' : 20,
            }}
            zIndex={10000000}
            >
            <Menu shadow="md" width={200}>
                <Menu.Target>
                    <Text size='md' p='md' color='dimmed'>
                        Edit
                    </Text>
                </Menu.Target>
                <Menu.Dropdown>
                    <Menu.Item
                        leftSection={<Camera height={18} />}
                        onClick={async () => {
                            sortByTaken();
                            setOpenEdit(false);
                        }}
                    >
                        Sort by date taken
                    </Menu.Item>
                    <Menu.Item
                        leftSection={<CloudUpload height={18} />}
                        onClick={async () => {
                            sortByUploaded();
                            setOpenEdit(false);
                        }}
                    >
                        Sort by date uploaded
                    </Menu.Item>
                    <Menu.Item
                        leftSection={<Trash height={18} />}
                        onClick={async () => setOpenEdit(!openEdit)}
                    >
                        Delete Photos
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>
        </Affix>}
    </>);
}
